import Phaser from 'phaser';
import { COLORS } from '../constants';

// ============================================
// Pathing System with Curved Paths & Multi-Route
// Based on towers_monsters_paths_design.md
// ============================================

// Route definition for multi-path support
export interface RouteData {
  id: string;
  points: Array<{ x: number; y: number }>;
  controlPoints?: Array<{ x: number; y: number }>;  // For curves
  segmentLengths: number[];
  totalLength: number;
}

// Path data structure with multi-route support
export interface PathData {
  id: string;
  routes: Map<string, RouteData>;
  distribution?: Record<string, number>;  // Route probability distribution
  useCurves: boolean;
  curveResolution: number;  // Points per curve segment
}

// Point on path with interpolation info
export interface PathPoint {
  x: number;
  y: number;
  segmentIndex: number;
  segmentProgress: number;
  totalProgress: number;
  angle: number;
}

// PathFollower component data
export interface PathFollowerState {
  pathId: string;
  routeId: string;
  distanceAlongPath: number;
  speed: number;
  reachedEnd: boolean;
}

class PathingSystemManager {
  private paths: Map<string, PathData> = new Map();
  private debugGraphics: Phaser.GameObjects.Graphics | null = null;
  private scene: Phaser.Scene | null = null;

  init(scene: Phaser.Scene): void {
    this.scene = scene;
  }

  // ============================================
  // Path Registration
  // ============================================

  /**
   * Register a simple path (single route, optional curves)
   */
  registerPath(pathData: {
    id: string;
    points: Array<{ x: number; y: number }>;
    totalLength: number;
    segmentLengths: number[];
  }): void {
    const mainRoute: RouteData = {
      id: 'main',
      points: pathData.points,
      segmentLengths: pathData.segmentLengths,
      totalLength: pathData.totalLength,
    };

    const path: PathData = {
      id: pathData.id,
      routes: new Map([['main', mainRoute]]),
      useCurves: false,
      curveResolution: 10,
    };

    this.paths.set(path.id, path);
    console.log(`[PathingSystem] Registered path: ${path.id} (length: ${pathData.totalLength.toFixed(0)})`);
  }

  /**
   * Register multiple paths
   */
  registerPaths(pathsData: Array<{
    id: string;
    points: Array<{ x: number; y: number }>;
    totalLength: number;
    segmentLengths: number[];
  }>): void {
    pathsData.forEach(path => this.registerPath(path));
  }

  /**
   * Register a curved path using Catmull-Rom spline
   */
  registerCurvedPath(id: string, controlPoints: Array<{ x: number; y: number }>, resolution: number = 10): void {
    const curvedPoints = this.generateCatmullRomPath(controlPoints, resolution);
    const { segmentLengths, totalLength } = this.calculatePathLengths(curvedPoints);

    const mainRoute: RouteData = {
      id: 'main',
      points: curvedPoints,
      controlPoints,
      segmentLengths,
      totalLength,
    };

    const path: PathData = {
      id,
      routes: new Map([['main', mainRoute]]),
      useCurves: true,
      curveResolution: resolution,
    };

    this.paths.set(id, path);
    console.log(`[PathingSystem] Registered curved path: ${id} (length: ${totalLength.toFixed(0)}, ${curvedPoints.length} points)`);
  }

  /**
   * Register a multi-route path with distribution
   */
  registerMultiRoutePath(
    id: string,
    routes: Record<string, Array<{ x: number; y: number }>>,
    distribution: Record<string, number>,
    useCurves: boolean = false,
    resolution: number = 10
  ): void {
    const routeMap = new Map<string, RouteData>();

    for (const [routeId, controlPoints] of Object.entries(routes)) {
      let points: Array<{ x: number; y: number }>;

      if (useCurves) {
        points = this.generateCatmullRomPath(controlPoints, resolution);
      } else {
        points = controlPoints;
      }

      const { segmentLengths, totalLength } = this.calculatePathLengths(points);

      routeMap.set(routeId, {
        id: routeId,
        points,
        controlPoints: useCurves ? controlPoints : undefined,
        segmentLengths,
        totalLength,
      });
    }

    const path: PathData = {
      id,
      routes: routeMap,
      distribution,
      useCurves,
      curveResolution: resolution,
    };

    this.paths.set(id, path);
    console.log(`[PathingSystem] Registered multi-route path: ${id} with ${routeMap.size} routes`);
  }

  /**
   * Get a registered path
   */
  getPath(pathId: string): PathData | undefined {
    return this.paths.get(pathId);
  }

  /**
   * Clear all registered paths
   */
  clearPaths(): void {
    this.paths.clear();
  }

  // ============================================
  // Catmull-Rom Spline Generation
  // ============================================

  /**
   * Generate smooth path using Catmull-Rom spline interpolation
   */
  private generateCatmullRomPath(
    controlPoints: Array<{ x: number; y: number }>,
    resolution: number
  ): Array<{ x: number; y: number }> {
    if (controlPoints.length < 2) return [...controlPoints];

    const result: Array<{ x: number; y: number }> = [];

    // Add phantom points at start and end for smooth curves
    const points = [
      controlPoints[0], // Duplicate first point
      ...controlPoints,
      controlPoints[controlPoints.length - 1], // Duplicate last point
    ];

    // Generate points along each segment
    for (let i = 1; i < points.length - 2; i++) {
      const p0 = points[i - 1];
      const p1 = points[i];
      const p2 = points[i + 1];
      const p3 = points[i + 2];

      for (let j = 0; j < resolution; j++) {
        const t = j / resolution;
        const point = this.catmullRomPoint(p0, p1, p2, p3, t);
        result.push(point);
      }
    }

    // Add final point
    result.push(controlPoints[controlPoints.length - 1]);

    return result;
  }

  /**
   * Calculate a single point on a Catmull-Rom spline
   */
  private catmullRomPoint(
    p0: { x: number; y: number },
    p1: { x: number; y: number },
    p2: { x: number; y: number },
    p3: { x: number; y: number },
    t: number,
    tension: number = 0.5
  ): { x: number; y: number } {
    const t2 = t * t;
    const t3 = t2 * t;

    const x = tension * (
      (2 * p1.x) +
      (-p0.x + p2.x) * t +
      (2 * p0.x - 5 * p1.x + 4 * p2.x - p3.x) * t2 +
      (-p0.x + 3 * p1.x - 3 * p2.x + p3.x) * t3
    );

    const y = tension * (
      (2 * p1.y) +
      (-p0.y + p2.y) * t +
      (2 * p0.y - 5 * p1.y + 4 * p2.y - p3.y) * t2 +
      (-p0.y + 3 * p1.y - 3 * p2.y + p3.y) * t3
    );

    return { x, y };
  }

  /**
   * Calculate segment lengths and total length
   */
  private calculatePathLengths(points: Array<{ x: number; y: number }>): {
    segmentLengths: number[];
    totalLength: number;
  } {
    const segmentLengths: number[] = [];
    let totalLength = 0;

    for (let i = 1; i < points.length; i++) {
      const dx = points[i].x - points[i - 1].x;
      const dy = points[i].y - points[i - 1].y;
      const len = Math.sqrt(dx * dx + dy * dy);
      segmentLengths.push(len);
      totalLength += len;
    }

    return { segmentLengths, totalLength };
  }

  // ============================================
  // Route Selection
  // ============================================

  /**
   * Select a random route based on distribution
   */
  selectRoute(pathId: string): string {
    const path = this.paths.get(pathId);
    if (!path) return 'main';

    if (!path.distribution || path.routes.size === 1) {
      return 'main';
    }

    // Random selection based on distribution
    const rand = Math.random();
    let cumulative = 0;

    for (const [routeId, probability] of Object.entries(path.distribution)) {
      cumulative += probability;
      if (rand <= cumulative) {
        return routeId;
      }
    }

    // Fallback to first route
    return path.routes.keys().next().value || 'main';
  }

  // ============================================
  // Path Following Logic
  // ============================================

  /**
   * Create a new path follower state
   */
  createFollower(pathId: string, speed: number, routeId?: string): PathFollowerState {
    const selectedRoute = routeId || this.selectRoute(pathId);
    return {
      pathId,
      routeId: selectedRoute,
      distanceAlongPath: 0,
      speed,
      reachedEnd: false,
    };
  }

  /**
   * Update a path follower (call each frame)
   */
  updateFollower(follower: PathFollowerState, delta: number): PathPoint | null {
    if (follower.reachedEnd) return null;

    const path = this.paths.get(follower.pathId);
    if (!path) {
      console.warn(`[PathingSystem] Path not found: ${follower.pathId}`);
      return null;
    }

    const route = path.routes.get(follower.routeId);
    if (!route) {
      console.warn(`[PathingSystem] Route not found: ${follower.routeId}`);
      return null;
    }

    // Move along path
    follower.distanceAlongPath += follower.speed * (delta / 1000);

    // Check if reached end
    if (follower.distanceAlongPath >= route.totalLength) {
      follower.distanceAlongPath = route.totalLength;
      follower.reachedEnd = true;
    }

    return this.getPointAtDistanceOnRoute(route, follower.distanceAlongPath);
  }

  /**
   * Sample a point along a route at a given distance
   */
  private getPointAtDistanceOnRoute(route: RouteData, distance: number): PathPoint {
    distance = Math.max(0, Math.min(distance, route.totalLength));

    let accumulatedDistance = 0;
    let segmentIndex = 0;

    for (let i = 0; i < route.segmentLengths.length; i++) {
      if (accumulatedDistance + route.segmentLengths[i] >= distance) {
        segmentIndex = i;
        break;
      }
      accumulatedDistance += route.segmentLengths[i];
      segmentIndex = i;
    }

    if (segmentIndex >= route.points.length - 1) {
      segmentIndex = route.points.length - 2;
    }

    const p1 = route.points[segmentIndex];
    const p2 = route.points[segmentIndex + 1];
    const segmentLength = route.segmentLengths[segmentIndex];

    const distanceInSegment = distance - accumulatedDistance;
    const segmentProgress = segmentLength > 0 ? distanceInSegment / segmentLength : 0;

    const x = p1.x + (p2.x - p1.x) * segmentProgress;
    const y = p1.y + (p2.y - p1.y) * segmentProgress;
    const angle = Math.atan2(p2.y - p1.y, p2.x - p1.x);

    return {
      x,
      y,
      segmentIndex,
      segmentProgress,
      totalProgress: distance / route.totalLength,
      angle,
    };
  }

  /**
   * Get position along a path at a specific distance
   */
  getPositionAlongPath(pathId: string, distance: number, routeId: string = 'main'): { x: number; y: number } | null {
    const path = this.paths.get(pathId);
    if (!path) return null;

    const route = path.routes.get(routeId);
    if (!route) return null;

    const point = this.getPointAtDistanceOnRoute(route, distance);
    return { x: point.x, y: point.y };
  }

  /**
   * Get the total length of a path
   */
  getPathLength(pathId: string, routeId: string = 'main'): number {
    const path = this.paths.get(pathId);
    if (!path) return 0;

    const route = path.routes.get(routeId);
    return route?.totalLength ?? 0;
  }

  /**
   * Get the starting point of a path
   */
  getPathStart(pathId: string, routeId: string = 'main'): { x: number; y: number } | null {
    const path = this.paths.get(pathId);
    if (!path) return null;

    const route = path.routes.get(routeId);
    if (!route || route.points.length === 0) return null;

    return { ...route.points[0] };
  }

  /**
   * Get the ending point of a path
   */
  getPathEnd(pathId: string, routeId: string = 'main'): { x: number; y: number } | null {
    const path = this.paths.get(pathId);
    if (!path) return null;

    const route = path.routes.get(routeId);
    if (!route || route.points.length === 0) return null;

    return { ...route.points[route.points.length - 1] };
  }

  /**
   * Get all route IDs for a path
   */
  getRouteIds(pathId: string): string[] {
    const path = this.paths.get(pathId);
    if (!path) return [];
    return Array.from(path.routes.keys());
  }

  // ============================================
  // Debug Rendering
  // ============================================

  /**
   * Enable debug rendering for paths
   */
  enableDebugDraw(graphics: Phaser.GameObjects.Graphics): void {
    this.debugGraphics = graphics;
  }

  /**
   * Draw all registered paths (for debugging)
   */
  drawDebugPaths(): void {
    if (!this.debugGraphics) return;

    this.debugGraphics.clear();

    this.paths.forEach((path, id) => {
      path.routes.forEach((route, routeId) => {
        this.drawRoute(route, path.useCurves);
      });
    });
  }

  /**
   * Draw a single route
   */
  private drawRoute(route: RouteData, isCurved: boolean): void {
    if (!this.debugGraphics || route.points.length < 2) return;

    // Draw path line
    const color = isCurved ? 0x00ff00 : COLORS.PATH;
    this.debugGraphics.lineStyle(4, color, 0.8);
    this.debugGraphics.beginPath();
    this.debugGraphics.moveTo(route.points[0].x, route.points[0].y);

    for (let i = 1; i < route.points.length; i++) {
      this.debugGraphics.lineTo(route.points[i].x, route.points[i].y);
    }
    this.debugGraphics.strokePath();

    // Draw control points if curved
    if (isCurved && route.controlPoints) {
      route.controlPoints.forEach((point, index) => {
        const isStart = index === 0;
        const isEnd = index === route.controlPoints!.length - 1;

        if (isStart) {
          this.debugGraphics!.fillStyle(0x00ff00, 1);
        } else if (isEnd) {
          this.debugGraphics!.fillStyle(0xff0000, 1);
        } else {
          this.debugGraphics!.fillStyle(0x0000ff, 1);
        }

        this.debugGraphics!.fillCircle(point.x, point.y, 8);
      });
    }

    // Draw waypoints
    route.points.forEach((point, index) => {
      if (index % 5 === 0) {  // Draw every 5th point to avoid clutter
        this.debugGraphics!.fillStyle(0xffff00, 0.5);
        this.debugGraphics!.fillCircle(point.x, point.y, 3);
      }
    });

    // Draw direction arrows
    const arrowInterval = 100;
    const numArrows = Math.floor(route.totalLength / arrowInterval);

    this.debugGraphics.fillStyle(0xffffff, 0.6);
    for (let i = 1; i < numArrows; i++) {
      const point = this.getPointAtDistanceOnRoute(route, i * arrowInterval);
      this.drawArrow(point.x, point.y, point.angle);
    }
  }

  /**
   * Draw a direction arrow
   */
  private drawArrow(x: number, y: number, angle: number): void {
    if (!this.debugGraphics) return;

    const size = 8;
    const p1x = x + Math.cos(angle) * size;
    const p1y = y + Math.sin(angle) * size;
    const p2x = x + Math.cos(angle + 2.5) * size * 0.6;
    const p2y = y + Math.sin(angle + 2.5) * size * 0.6;
    const p3x = x + Math.cos(angle - 2.5) * size * 0.6;
    const p3y = y + Math.sin(angle - 2.5) * size * 0.6;

    this.debugGraphics.fillTriangle(p1x, p1y, p2x, p2y, p3x, p3y);
  }

  destroy(): void {
    this.clearPaths();
    this.debugGraphics = null;
    this.scene = null;
  }
}

// Singleton instance
export const PathingSystem = new PathingSystemManager();
